import { FC, useContext, useEffect, useState } from "react";
import GlobalContext from "src/context/global-context";
import { useMutation } from "react-query";
import client from "../../api";
import moment from "moment";
import { User } from "src/api/types";
import ToggleSwitch from "src/components/ui/switch";
import {
  NetworkDisconnectMsg,
  APISomethingWrongMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import { capitalizeFirstLetter } from "src/lib/utils";
import { getErrorMessage } from "../../lib/utils";
import { Item } from "./user-item";
import Button from "../ui/button";
import usePermissions from "src/hooks/use-permissions";
import { EditIcon } from "src/components/icons/edit-icon";
import EditPhoneModal from "./edit-phone-modal";

type Props = {
  user: User;
};

interface VipStatusUpdateData {
  id: number;
  previous_status: "VIP" | "NORMAL";
  new_status: "VIP" | "NORMAL";
  is_vip: boolean;
}

interface VipStatusUpdateResponse {
  status: string;
  code: number;
  message: string;
  data: VipStatusUpdateData;
  items: unknown[];
  totalcount: number;
}

const UserDataTab: FC<Props> = ({ user }) => {
  const { setIsAlertOpened, setAlertText, setKey, key } =
    useContext(GlobalContext);
  const [isVip, setIsVip] = useState<boolean>(Boolean(user.is_vip));
  const [agencyStatus, setAgencyStatus] = useState<any>(
    user?.agency_status?.name || "",
  );
  const { canList: canList2FA, canEdit: canEdit2FA } = usePermissions([
    permissionConstants.UPDATE_USER_2FA_STATUS_METHOD,
    "update user 2fa status method",
  ]);
  const canShow2FA = Boolean(canList2FA && canEdit2FA);

  useEffect(() => {
    setIsVip(Boolean(user.is_vip));
  }, [user.id, user.is_vip]);
  useEffect(() => {
    setAgencyStatus(user?.agency_status?.name || "");
  }, [user?.agency_status?.name]);

  const getInitialPhone = (): string => {
    if (user?.data?.country_code && user?.data?.phone) {
      return `${user.data.country_code}${user.data.phone}`;
    }
    return user?.data?.phone ? String(user.data.phone) : "";
  };

  const [phoneNumber, setPhoneNumber] = useState<string>(getInitialPhone);
  const [isEditPhoneModalOpen, setIsEditPhoneModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    setPhoneNumber(getInitialPhone());
  }, [user?.id, user?.data?.country_code, user?.data?.phone]);
  /* ---------------- VIP UPDATE ---------------- */

  const { mutate: updateVip, isLoading: isLoadingVip } = useMutation<
    VipStatusUpdateResponse,
    unknown,
    {
      userId: number;
      input: {
        is_vip: boolean;
      };
    }
  >(
    user.is_business
      ? client.users.updateBuisinessVip
      : client.users.updateIndividualVip,
    {
      onSuccess: (response) => {
        setIsVip(response?.data?.is_vip);

        setAlertText(response.message);
        setIsAlertOpened(true);
      },
      onError: (error) => handleError(error),
    },
  );

  /* ---------------- 2FA UPDATE ---------------- */

  const convertToNumericStatus = (value: any) => {
    if (typeof value === "boolean") {
      return value ? 1 : 0;
    }
    if (typeof value === "string") {
      return value.toLowerCase() === "true" ? 1 : 0;
    }
    return Number(value) || 0;
  };

  const { mutate: update2FA } = useMutation(client.users.update2FAForUser, {
    onSuccess: (data) => {
      for (const key in data.data) {
        if (
          key === "phone_2fa_enabled" ||
          key === "email_2fa_enabled" ||
          key === "google2fa_enabled"
        ) {
          user[key] = convertToNumericStatus(data.data[key]);
        }
      }

      setAlertText(data?.message);
      setIsAlertOpened(true);
    },
    onError: (error: any) => handleError(error),
  });

  /* ---------------- AGENCY STATUS ---------------- */

  const { mutate: updateStatus, isLoading } = useMutation(
    client.users.updateIndividualAgencyStatus,
    {
      onSuccess: (data) => {
        setAlertText(data?.message);
        setAgencyStatus("approved");

        setIsAlertOpened(true);
        setKey(key + 1);
        window.location.reload();
      },
      onError: (error: any) => handleError(error),
    },
  );

  /* ---------------- COMMON ERROR HANDLER ---------------- */

  const handleError = (error: any) => {
    if (error.code === "ERR_NETWORK") {
      setAlertText(NetworkDisconnectMsg);
    } else {
      if (error.response) {
        setAlertText(getErrorMessage(error.response.data));
        if (error.response.data.message === "USER_ALREADY_VERIFIED_AS_AGENCY") {
          setAgencyStatus("approved");
        }
      } else {
        setAlertText(APISomethingWrongMsg);
      }
    }
    setIsAlertOpened(true);
  };

  /* ---------------- STATUS COLOR ---------------- */

  const getStatusColor = (status: string) => {
    switch (status) {
      case "verified":
      case "approved":
        return "#03BC70";
      case "unverified":
      case "rejected":
        return "#F44336";
      default:
        return "";
    }
  };

  /* =====================================================
      INDIVIDUAL USER UI
  ===================================================== */

  const individualFields = (
    <div className="grid grid-cols-3 mt-6">
      <div>
        <div className="grid grid-cols-3">
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                Level:
              </p>
              <div className="flex items-center gap-3">
                <p className="text-primary font-manrope text-[14px] font-medium flex-shrink">
                  VIP
                </p>
                <ToggleSwitch
                  type="vip"
                  value={isVip}
                  disabled={isLoadingVip}
                  onValueChanged={() =>
                    updateVip({
                      userId: user.id,
                      input: {
                        is_vip: !isVip,
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 mt-6">
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                KYC status:
              </p>
              <p className="text-primary font-manrope text-[14px] font-medium flex-shrink">
                <span style={{ color: getStatusColor(user.kyc_status?.name) }}>
                  {user?.kyc_status == null ||
                  user?.kyc_status?.name === "unverified"
                    ? "Not verified"
                    : capitalizeFirstLetter(
                        user?.kyc_status?.name.replaceAll("_", " "),
                      )}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 mt-6">
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                AML Status:
              </p>
              <p className="text-primary font-manrope text-[14px] font-medium flex-shrink">
                <span style={{ color: getStatusColor(user.status?.name) }}>
                  {user.status?.name === "unverified"
                    ? "Not verified"
                    : capitalizeFirstLetter(
                        user.status?.name.replaceAll("_", " "),
                      )}
                </span>
              </p>
            </div>
          </div>
        </div>
        {user.kyc_status?.name === "unverified" ||
        String(user.data.country).toLowerCase() !== "georgia" ? (
          ""
        ) : (
          <div className="grid grid-cols-3 mt-6">
            <div className="flex flex-col gap-6">
              <div>
                <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                  Agency Status:
                </p>
                <p className="text-primary font-manrope text-[14px] font-medium flex-shrink">
                  {agencyStatus ? (
                    <span
                      style={{
                        color: getStatusColor(agencyStatus),
                      }}
                    >
                      {agencyStatus === "unverified"
                        ? "Not verified"
                        : capitalizeFirstLetter(
                            agencyStatus.replaceAll("_", " "),
                          )}
                    </span>
                  ) : (
                    ""
                  )}
                </p>
                <div>
                  {user?.kyc_status?.name === "approved" &&
                    agencyStatus === "unverified" && (
                      <Button
                        variant="validate"
                        type="submit"
                        isLoading={isLoading}
                        className="w-full rounded-[10px] font-manrope text-[16px] font-bold leading-[24px] h-[45px]"
                        onClick={() =>
                          updateStatus({
                            id: user?.id,
                          })
                        }
                      >
                        {!isLoading && "Verify"}
                      </Button>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}
        <Item title="Risk Level" value={user.risk_level} />
        <Item title="Document type" value={user.data?.document_type} />
        <Item title="Document number" value={user.data?.document_number} />
        <Item
          title="Document Issued date"
          // value={user.data?.document_date_of_issue}
          value={
            user?.data?.document_date_of_issue &&
            moment(user?.data?.document_date_of_issue).format(
              "YYYY-MM-DD HH:mm:ss",
            )
          }
        />
        <Item
          title="Document expiration date"
          // value={user.data?.document_date_of_expiration}
          value={
            user?.data?.document_date_of_expiration &&
            moment(user?.data?.document_date_of_expiration).format(
              "YYYY-MM-DD HH:mm:ss",
            )
          }
        />
        <br />
        {canShow2FA && (
          <>
            <div className="flex items-center gap-3">
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                Email 2FA Status :
              </p>
              <div className="flex items-center gap-3">
                <ToggleSwitch
                  type="vip"
                  value={!!user.email_2fa_enabled}
                  disabled={!canEdit2FA}
                  onValueChanged={() =>
                    update2FA({
                      userId: user.id,
                      input: {
                        email_2fa_enabled: !user.email_2fa_enabled,
                      },
                    })
                  }
                />
              </div>
            </div>
            <br />
            <div className="flex items-center gap-3">
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                Phone 2FA Status :
              </p>
              <div className="flex items-center gap-3">
                <ToggleSwitch
                  type="vip"
                  value={!!user.phone_2fa_enabled}
                  disabled={!canEdit2FA}
                  onValueChanged={() =>
                    update2FA({
                      userId: user.id,
                      input: {
                        phone_2fa_enabled: !user.phone_2fa_enabled,
                      },
                    })
                  }
                />
              </div>
            </div>
            <br />
            <div className="flex items-center gap-3">
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                Google 2FA Status :
              </p>
              <div className="flex items-center gap-3">
                <ToggleSwitch
                  type="vip"
                  value={!!user.google2fa_enabled}
                  disabled={!canEdit2FA}
                  onValueChanged={() =>
                    update2FA({
                      userId: user.id,
                      input: {
                        google2fa_enabled: !user.google2fa_enabled,
                      },
                    })
                  }
                />
              </div>
            </div>
            <br />
          </>
        )}
        {/* {canShow2FA && (
            <div className="flex items-center gap-3">
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                Android biometric :
              </p>
              <div className="flex items-center gap-3">
                <ToggleSwitch
                  type="vip"
                  value={!!user.android_biometric_enabled}
                  onValueChanged={() =>
                    update2FA({
                      userId: user.id,
                      input: {
                        android_biometric_enabled:
                          !user.android_biometric_enabled ? "true" : "false",
                      },
                    })
                  }
                />
              </div>
            </div>
          )}
        <br />
        {ConfigValue.PUBLIC_AUTH_2FA_USER_ID &&
          user?.id == Number(ConfigValue.PUBLIC_AUTH_2FA_USER_ID) && (
            <div className="flex items-center gap-3">
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                iOS biometric :
              </p>
              <div className="flex items-center gap-3">
                <ToggleSwitch
                  type="vip"
                  value={!!user.ios_biometric_enabled}
                  onValueChanged={() =>
                    update2FA({
                      userId: user.id,
                      input: {
                        ios_biometric_enabled: !user.ios_biometric_enabled
                          ? "true"
                          : "false",
                      },
                    })
                  }
                />
              </div>
            </div>
          )} */}
      </div>
      <div>
        <Item title="First Name" value={user.data.first_name} />
        <Item title="Last Name" value={user.data.last_name} />
        <Item title="Gender" value={user.data.gender} />
        {/* document_number
document_type
document_date_of_expiration 
document_date_of_issue */}
        <Item
          title="Address"
          value={`${user.data?.post_code ? `${user.data?.post_code},` : ""}${
            user.data?.city ? `${user.data?.city},` : ""
          }${user.data?.street || ""}`}
        />
        <Item
          title="Country"
          value={
            user?.data?.country
              ? user?.data?.country
              : user?.data?.source_country
          }
        />
        <Item
          title="Registration Date"
          value={
            user?.data?.created_at &&
            moment(user?.data?.created_at).format("YYYY-MM-DD HH:mm:ss")
          }
        />
        <Item
          title="Verification Date"
          value={
            user?.verification_completed_at
              ? moment(user?.verification_completed_at).format(
                  "YYYY-MM-DD HH:mm:ss",
                )
              : "__"
          }
        />
        <Item
          title="Last Login"
          value={
            user?.data?.latest_login_histories?.length > 0 &&
            user?.data?.latest_login_histories[0].logged_at &&
            moment
              .unix(Number(user?.latest_login_histories[0].logged_at))
              .format("YYYY-MM-DD HH:mm:ss")
          }
        />
      </div>
      <div>
        <Item
          title="Phone Number"
          value={phoneNumber || "__"}
          action={
            <button
              type="button"
              onClick={() => setIsEditPhoneModalOpen(true)}
              className="p-1 rounded-md text-secondary hover:text-brand hover:bg-[#F4F5F6] dark:hover:bg-[#23262F] transition-colors focus:outline-none"
              title="Edit Individual Phone Number"
            >
              <EditIcon className="w-[14px] h-[14px]" />
            </button>
          }
        />
        <Item
          title="Phone Verification date"
          value={
            user?.phone_verified_at
              ? moment
                  .unix(user?.phone_verified_at)
                  .format("YYYY-MM-DD HH:mm:ss")
              : "__"
          }
        />

        <Item title="Date of Birth" value={user.data?.date_of_birth} />
        <Item title="Email" value={user.email} />
        <Item title="Personal code" value={user?.data?.personal_code} />
        <Item title="IP Address" value={user?.data?.source_ip} />
        <Item title="Device" value={user?.data?.source_type} />
      </div>
    </div>
  );

  /* =====================================================
      BUSINESS USER UI
  ===================================================== */

  const buisinessFields = (
    <div className="grid grid-cols-3 mt-6">
      <div>
        <div className="grid grid-cols-3">
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                Level:
              </p>
              <div className="flex items-center gap-3">
                <p className="text-primary font-manrope text-[14px] font-medium flex-shrink">
                  VIP
                </p>
                <ToggleSwitch
                  type="vip"
                  value={isVip}
                  disabled={isLoadingVip}
                  onValueChanged={() =>
                    updateVip({
                      userId: user.id,
                      input: {
                        is_vip: !isVip,
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 mt-6">
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                KYC status:
              </p>
              <p className="text-primary font-manrope text-[14px] font-medium flex-shrink">
                <span style={{ color: getStatusColor(user.kyc_status?.name) }}>
                  {user?.kyc_status == null ||
                  user.kyc_status?.name === "unverified"
                    ? "Not verified"
                    : capitalizeFirstLetter(
                        user.kyc_status?.name.replaceAll("_", " "),
                      )}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 mt-6">
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-secondary font-manrope text-[14px] font-medium w-[100px] sm:w-[140px] flex-shrink-0">
                AML Status:
              </p>
              <p className="text-primary font-manrope text-[14px] font-medium flex-shrink">
                <span style={{ color: getStatusColor(user.status?.name) }}>
                  {user.status?.name === "unverified"
                    ? "Not verified"
                    : capitalizeFirstLetter(
                        user.status?.name.replaceAll("_", " "),
                      )}
                </span>
              </p>
            </div>
          </div>
        </div>
        <Item
          title="Director's first name"
          value={user.aml_business_data?.director_first_name}
        />
        <Item
          title="Director's last name"
          value={user.aml_business_data?.director_last_name}
        />
        <Item
          title="Country"
          value={
            user?.data?.country
              ? user?.data?.country
              : user?.data?.source_country
          }
        />{" "}
      </div>
      <div>
        <Item
          title="Director's TIN"
          value={user.aml_business_data?.director_tin}
        />
        <Item
          title="Legal address"
          value={user.aml_business_data?.legal_address}
        />
        <Item
          title="Actual address"
          value={user.aml_business_data?.real_address}
        />
        <Item
          title="Phone Number"
          value={phoneNumber || "__"}
          action={
            <button
              type="button"
              onClick={() => setIsEditPhoneModalOpen(true)}
              className="p-1 rounded-md text-secondary hover:text-brand hover:bg-[#F4F5F6] dark:hover:bg-[#23262F] transition-colors focus:outline-none"
              title="Edit Business Phone Number"
            >
              <EditIcon className="w-[14px] h-[14px]" />
            </button>
          }
        />{" "}
        <Item
          title="Phone Verification date"
          value={
            user?.phone_verified_at
              ? moment
                  .unix(user?.phone_verified_at)
                  .format("YYYY-MM-DD HH:mm:ss")
              : "__"
          }
        />
        <Item
          title="Registration Date"
          value={
            user?.data?.created_at &&
            moment(user?.data?.created_at).format("YYYY-MM-DD HH:mm:ss")
          }
        />
        <Item
          title="Verification Date"
          value={
            user?.verification_completed_at
              ? moment(user?.verification_completed_at).format(
                  "YYYY-MM-DD HH:mm:ss",
                )
              : "__"
          }
        />
        <Item
          title="Last Login"
          value={
            user?.latestLoginHistories &&
            user?.latestLoginHistories?.length > 0 &&
            user?.latestLoginHistories[0]?.logged_at
              ? moment
                  .unix(Number(user?.latestLoginHistories[0]?.logged_at))
                  .format("YYYY-MM-DD HH:mm:ss")
              : "__"
          }
        />
      </div>
      <div>
        <Item title="Tax code" value={user.aml_business_data?.tax_code} />
        <Item title="Date of Birth" value={user.data?.date_of_birth} />
        <Item title="Email" value={user.email} />
        <Item
          title="Company name"
          value={user.aml_business_data?.company_name}
        />
        <Item title="IP Address" value={user?.data?.source_ip} />
        <Item title="Device" value={user?.data?.source_type} />
      </div>
    </div>
  );

  /* ===================================================== */

  return (
    <>
      {user.is_business ? buisinessFields : individualFields}
      <EditPhoneModal
        open={isEditPhoneModalOpen}
        onClose={() => setIsEditPhoneModalOpen(false)}
        currentPhone={phoneNumber}
        userId={user.id}
        isBusiness={Boolean(user.is_business)}
        onSuccess={(newPhone) => {
          setPhoneNumber(newPhone);
          if (user.data) {
            user.data.phone = newPhone;
            user.data.country_code = "";
          }
        }}
      />
    </>
  );
};

export default UserDataTab;
